import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../services/api.js';
import {
  type IdentityKind,
  type IdentityNumberSummary,
  displayNumber,
  identityKindLabel,
} from '../../utils/identityNumber.js';
import { IdentityNumberDialog } from './IdentityNumberDialog.js';

/**
 * 366 (#1223, US3, design 1f) — a customer's IC number on the Profile card. Masked by default; Reveal asks the
 * platform, which records who revealed it. Change / Clear go through the dialog (1g). Absent for an account that is
 * never asked (staff). Only staff who may manage this account get Reveal and Change.
 */
export function IdentityNumberRow({
  userId,
  summary,
  required,
  canManage,
}: {
  userId: string;
  summary: IdentityNumberSummary | null;
  required: boolean;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [revealed, setRevealed] = useState<{ kind: IdentityKind; number: string } | null>(null);
  const [changing, setChanging] = useState(false);

  const reveal = useMutation({
    mutationFn: () =>
      api.post<{ kind: IdentityKind; country: string; number: string }>(
        `/users/${userId}/identity-number/reveal`,
      ),
    onSuccess: (data) => {
      setRevealed({ kind: data.kind, number: data.number });
      void queryClient.invalidateQueries({ queryKey: ['user-events', userId] });
    },
  });

  if (!summary && !required) return null;

  return (
    <>
      <dt className="text-steel">IC number</dt>
      <dd data-testid="user-identity-number" className="flex flex-wrap items-center gap-2">
        {summary ? (
          <>
            <span className="font-mono">{revealed ? displayNumber(revealed) : summary.masked}</span>
            <span className="rounded bg-surface-soft px-1.5 py-0.5 text-xs text-steel">
              {identityKindLabel(summary)}
            </span>
            {canManage &&
              (revealed ? (
                <button
                  type="button"
                  onClick={() => setRevealed(null)}
                  className="text-xs text-primary hover:underline"
                >
                  Hide
                </button>
              ) : (
                <button
                  type="button"
                  disabled={reveal.isPending}
                  onClick={() => reveal.mutate()}
                  className="text-xs text-primary hover:underline"
                >
                  Reveal
                </button>
              ))}
          </>
        ) : (
          <span className="text-steel">Not given yet — asked on their next visit.</span>
        )}
        {canManage && (
          <button
            type="button"
            onClick={() => setChanging(true)}
            className="text-xs text-primary hover:underline"
          >
            Change
          </button>
        )}
        {revealed && (
          <span data-testid="user-identity-revealed" className="basis-full text-xs text-steel">
            Revealed by you just now — recorded in this account's history.
          </span>
        )}
        {reveal.isError && (
          <span role="alert" className="basis-full text-xs text-semantic-danger-text">
            {(reveal.error as { message?: string }).message ?? 'Could not reveal the number.'}
          </span>
        )}
      </dd>
      {changing && (
        <IdentityNumberDialog
          userId={userId}
          hasNumber={summary !== null}
          onClose={() => {
            setChanging(false);
            setRevealed(null);
          }}
        />
      )}
    </>
  );
}
